import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useLocalization } from '@shared-ui/localization-context';
import { UitkCalendarNavigationControls } from './uitk-calendar-navigation-controls.esm.js';
import { UitkBaseCalendar } from '../CalendarBase/uitk-calendar.esm.js';
import dateUtils from '../CalendarBase/calendar-utils/dateUtils.esm.js';
import { getMonthYear } from '../generic-utils/dateUtils.esm.js';
import getAriaLiveLabel from '../generic-utils/ariaLiveLabelUtils.esm.js';
var usePrevious = function (value) {
  var ref = React.useRef(null);
  React.useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};
var UitkCalendarNavigationPagination = function (props) {
  var className = props.className,
    _a = props.handleShape,
    handleShape = _a === void 0 ? 'rectangle' : _a,
    isSingleDateSelection = props.isSingleDateSelection,
    _b = props.locale,
    locale = _b === void 0 ? 'en-US' : _b,
    _c = props.maxDate,
    maxDate = _c === void 0 ? null : _c,
    _d = props.minDate,
    minDate = _d === void 0 ? new Date() : _d,
    monthProp = props.month,
    _e = props.navType,
    navType = _e === void 0 ? 'outset' : _e,
    _f = props.numMonthsToDisplay,
    numMonthsToDisplay = _f === void 0 ? 2 : _f,
    onHoverDay = props.onHoverDay,
    onSelectionChanged = props.onSelectionChanged,
    _g = props.padWeeks,
    padWeeks = _g === void 0 ? false : _g,
    selectable = props.selectable,
    selectionChangeMode = props.selectionChangeMode,
    selectionEnd = props.selectionEnd,
    selectionStart = props.selectionStart,
    stretchy = props.stretchy,
    yearProp = props.year,
    numColumns = props.numColumns,
    _h = props.numMonthsToAdvance,
    numMonthsToAdvance = _h === void 0 ? 1 : _h,
    onNextClicked = props.onNextClicked,
    onPrevClicked = props.onPrevClicked,
    rest = __rest(props, ["className", "handleShape", "isSingleDateSelection", "locale", "maxDate", "minDate", "month", "navType", "numMonthsToDisplay", "onHoverDay", "onSelectionChanged", "padWeeks", "selectable", "selectionChangeMode", "selectionEnd", "selectionStart", "stretchy", "year", "numColumns", "numMonthsToAdvance", "onNextClicked", "onPrevClicked"]);
  var l10n = useLocalization();
  var _j = getMonthYear(selectionStart, null, {
      month: monthProp,
      year: yearProp
    }),
    defaultMonth = _j.month,
    defaultYear = _j.year;
  var _k = __read(React.useState(defaultMonth), 2),
    month = _k[0],
    setMonth = _k[1];
  var _l = __read(React.useState(defaultYear), 2),
    year = _l[0],
    setYear = _l[1];
  var prevMonth = usePrevious(monthProp);
  var prevYear = usePrevious(yearProp);
  React.useEffect(function () {
    if (typeof monthProp === 'number' && typeof yearProp === 'number') {
      if (prevMonth !== monthProp || prevYear !== yearProp) {
        setMonth(monthProp);
        setYear(yearProp);
      }
    }
  }, [monthProp, yearProp, prevMonth, prevYear]);
  var ariaLiveLabel = React.useMemo(function () {
    return getAriaLiveLabel(month, year, numMonthsToDisplay, l10n);
  }, [month, year, numMonthsToDisplay, l10n]);
  var getControlState = function (_month, _year) {
    var isWithinBounds = function (boundaryDate, minOrMax) {
      // Normalize the min/max Date and current Date so we can compare the 1st of each month
      // When advancing, offset the number of months so maxDate stops in last column
      // Once the current Date tries to exceed the min/max Date, return false
      var isMin = minOrMax === 'min';
      var offset = isMin ? 0 : numMonthsToDisplay - 1;
      var monthDate = new Date(_year, _month + offset).getTime();
      var boundaryMonthDate = new Date(boundaryDate.getFullYear(), boundaryDate.getMonth()).getTime();
      return isMin ? monthDate > boundaryMonthDate : monthDate < boundaryMonthDate;
    };
    var _canRetreat = minDate ? isWithinBounds(minDate, 'min') : true;
    var _canAdvance = maxDate ? isWithinBounds(maxDate, 'max') : true;
    return {
      canAdvance: _canAdvance,
      canRetreat: _canRetreat
    };
  };
  var _m = getControlState(month, year),
    defaultCanAdvance = _m.canAdvance,
    defaultCanRetreat = _m.canRetreat;
  var _o = __read(React.useState(defaultCanAdvance), 2),
    canAdvance = _o[0],
    setCanAdvance = _o[1];
  var _p = __read(React.useState(defaultCanRetreat), 2),
    canRetreat = _p[0],
    setCanRetreat = _p[1];
  var handlePrevClicked = function () {
    var _a = dateUtils.addMonthsToMonth(year, month, -numMonthsToAdvance),
      updatedYear = _a.updatedYear,
      updatedMonth = _a.updatedMonth;
    var _b = getControlState(updatedMonth, updatedYear),
      _canRetreat = _b.canRetreat,
      _canAdvance = _b.canAdvance;
    // If target date is before min date, set date to minDate
    if (minDate && _canRetreat === false) {
      updatedMonth = minDate.getMonth();
      updatedYear = minDate.getFullYear();
    }
    setMonth(updatedMonth);
    setYear(updatedYear);
    setCanRetreat(_canRetreat);
    setCanAdvance(_canAdvance);
    onPrevClicked && onPrevClicked({
      month: updatedMonth,
      year: updatedYear
    });
  };
  var handleNextClicked = function () {
    var offset = 0;
    if (numMonthsToDisplay) offset = numMonthsToDisplay - 1 || 0;
    var _a = dateUtils.addMonthsToMonth(year, month, numMonthsToAdvance),
      updatedYear = _a.updatedYear,
      updatedMonth = _a.updatedMonth;
    var _b = getControlState(updatedMonth, updatedYear),
      _canRetreat = _b.canRetreat,
      _canAdvance = _b.canAdvance;
    // If target date is after max date, set date to maxDate
    if (maxDate && _canAdvance === false) {
      updatedMonth = maxDate.getMonth() - offset;
      updatedYear = maxDate.getFullYear();
    }
    setMonth(updatedMonth);
    setYear(updatedYear);
    setCanRetreat(_canRetreat);
    setCanAdvance(_canAdvance);
    onNextClicked && onNextClicked({
      month: updatedMonth,
      year: updatedYear
    });
  };
  // If the user is navigating the calendar days via arrow keys and not using the prev/next buttons,
  // then a callback is triggered within calendar-base to notify us when we need to move to the previous or next month.
  var handleArrowNavigation = function (direction) {
    // The right/down arrow key was used while on the last day of a last visible month,
    // so we'll fake the next button being click by calling handleNextClicked()
    if (canAdvance && (direction === 'ArrowRight' || direction === 'ArrowDown')) {
      // The left/up arrow key was used while on the first day of the first visible month,
      // so we'll fake a prev button being clicked by calling handlePrevClicked()
      handleNextClicked();
    } else if (canRetreat && (direction === 'ArrowLeft' || direction === 'ArrowUp')) {
      // The up arrow key was used while on the first day of the first visible month,
      // so we'll fake a prev button being clicked by calling handlePrevClicked()
      handlePrevClicked();
    }
  };
  var numMonths = numMonthsToDisplay;
  var numColumnsToDisplay = numColumns || numMonthsToDisplay;
  return React.createElement(React.Fragment, null, React.createElement("span", {
    className: "is-visually-hidden",
    "aria-live": "polite"
  }, ariaLiveLabel), React.createElement(UitkCalendarNavigationControls, {
    stretchy: stretchy,
    canAdvance: canAdvance,
    canRetreat: canRetreat,
    className: className,
    enabled: true,
    navType: navType,
    numMonths: numMonths,
    handleNextClicked: handleNextClicked,
    handlePrevClicked: handlePrevClicked
  }, React.createElement(UitkBaseCalendar, __assign({}, rest, {
    stretchy: stretchy,
    handleShape: handleShape,
    isSingleDateSelection: isSingleDateSelection,
    locale: locale,
    maxDate: maxDate !== null && maxDate !== void 0 ? maxDate : undefined,
    month: month,
    numColumns: numColumnsToDisplay,
    numMonthsToDisplay: numMonths,
    onArrowNavigation: handleArrowNavigation,
    onHoverDay: onHoverDay,
    onSelectionChanged: onSelectionChanged,
    padWeeks: padWeeks,
    selectable: selectable,
    selectionChangeMode: selectionChangeMode,
    selectionEnd: selectionEnd,
    selectionStart: selectionStart,
    year: year
  }))));
};
UitkCalendarNavigationPagination.displayName = 'UitkCalendarNavigationPagination';
export { UitkCalendarNavigationPagination };